"use client";

import * as X from "@/ui";
import { menu, useRoute, LanguageSelectorMobile, type MenuSection } from "./common";

import ZapptaxDefaultLogo from "@/ui/shared/assets/zapptax-default.svg";
import ZapptaxRetailLogo from "@/ui/shared/assets/zapptax-retail.svg";
import ZapptaxShippingLogo from "@/ui/shared/assets/zapptax-shipping.svg";


type Route = ReturnType<typeof useRoute>;

function getLogo(route: Route) {
  return (
    route[0] === "shipping" ? { image: ZapptaxShippingLogo, height: 44, alt: "Zapptax Shipping", to: "/shipping" as X.PageHref } :
    route[0] === "retail" ? { image: ZapptaxRetailLogo, height: 44, alt: "Zapptax Retail", to: "/retail" as X.PageHref } :
    { image: ZapptaxDefaultLogo, height: 24, alt: "Zapptax", to: "/" as X.PageHref }
  );
}


export function MenuMobile(props: X.PageProps<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  altHrefs?: { [k in X.Locale]: X.PageHref };
  withHeroImage?: boolean;
  isScrolled?: boolean;
}>) {
  return (
    <>
      <MobileHeader
        locale={props.locale}
        isScrolled={props.isScrolled}
        setMenuOpen={props.setIsOpen}
      />

      <MobileMenuPanel
        locale={props.locale}
        isOpen={props.isOpen}
        setIsOpen={props.setIsOpen}
        altHrefs={props.altHrefs}
      />

      {!props.withHeroImage && (
        <div className={X.clsx(
          "block xl:hidden h-header-mobile",
        )}/>
      )}
    </>
  );
}


function MobileHeader(props: X.PageProps<{
  setMenuOpen: (open: boolean) => void;
  isScrolled?: boolean;
  heroImage?: boolean;
}>) {
  const route = useRoute();
  const logo = getLogo(route);

  return (
    <header className={X.clsx(
      "xl:hidden flex justify-between items-center py-4 h-header-mobile pl-mobile-margin",
      props.isScrolled ? "bg-white/100" : props.heroImage ? "bg-white/0" : "bg-white/100",
      "transition-colors duration-300",
      "fixed top-0 right-0 left-0 z-30",
    )}>
      <X.Link href={logo.to} className="flex items-center">
        <X.Image
          src={logo.image}
          alt={logo.alt}
          height={logo.height}
        />
      </X.Link>
      <button onClick={() => props.setMenuOpen(true)} className="py-2 px-6">
        <X.Icon.Equal size={32} />
      </button>
    </header>
  )
}


function MobileMenuPanel(props: X.PageProps<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  altHrefs?: { [k in X.Locale]: X.PageHref };
}>) {
  const tr = X.useTranslator(props.locale);
  const downloadUrl = X.useDownloadUrl(props.locale);

  const closeMenu = () => props.setIsOpen(false);
  const [activeTab, setActiveTab] = X.useState<MenuSection>("travelers");

  const route = useRoute();
  const logo = getLogo(route);

  return (
    <div className={X.clsx(
      "block xl:hidden",
      "fixed inset-0 bg-white z-50 transform top-0 bottom-0 [-ms-overflow-style:none] [scrollbar-width:none]",
      "transition-transform duration-300 ease-in-out overflow-x-hidden w-dvw",
      props.isOpen ? "translate-x-0" : "-translate-x-full",
    )}>
      <div className="flex justify-between items-center py-4 pl-mobile-margin h-menu-mobile">
        <X.Link href="/" onClick={closeMenu} className="flex items-center">
          <X.Image
            src={logo.image}
            alt={logo.alt}
            height={logo.height}
            className="w-auto"
          />
        </X.Link>
        <button onClick={closeMenu} className="py-2 pr-6">
          <X.Icon.X size={28} />
        </button>
      </div>

      <div className="px-mobile-margin">
        <LanguageSelectorMobile
          locale={props.locale}
          altHrefs={props.altHrefs}
          backgroundColor="white"
        />
      </div>

      <div className="mt-12 mb-8 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className="flex space-x-2 px-mobile-margin">
          {Object.entries(menu).filter(([_, section]) => section.showMobile).map(([key, section]) => (
            <div key={key} onClick={() => setActiveTab(key as MenuSection)} className={X.clsx(
              "px-[9px] py-2 rounded-full text-[13px] border whitespace-nowrap",
              activeTab === key ? "bg-yellow border-yellow" : "border-black",
            )}>
              {tr(section.label)}
            </div>
          ))}
          <div>
            <div className="w-mobile-margin" />
          </div>
        </div>
      </div>

      <div className="px-mobile-margin">
        <div className={X.clsx("flex flex-col py-[4px] gap-2 overflow-x-hidden")}>
          {Object.entries(menu[activeTab]["sub"]).map(([key, sub]) => (
            sub.type === "button" ? (
              <div key={key} className="flex flex-row justify-start pt-2">
                <X.Button
                  secondary lg
                  label={tr(sub.label) as string}
                  exitHref={sub.exitHref as string}
                  href={sub.href as never}
                />
              </div>
            ) : (
              <X.Link
                key={key}
                href={sub.href}
                onClick={closeMenu}
                className={X.clsx(
                  "flex flex-row gap-2 items-center py-[4px]",
                  sub.type === "large" ? "text-[20px] leading-tight pb-4" : "text-[14px] font-medium pl-4",
                )}
              >
                <p>{tr(sub.label)}</p>
                {sub.type === "large" ? <X.Icon.MoveRight size={20} /> : <X.Icon.ChevronRight size={16} />}
              </X.Link>
            )
          ))}
          <div className="flex flex-col items-start mb-4 mt-10 gap-4">
            <X.Button
              primary lg
              label={tr({ en: "Get the app", fr: "Téléchargez l'application" })}
              exitHref={downloadUrl}
            />
            {activeTab === "retail" && (
              <X.Button
                tertiary lg

                //exitHref="https://my.zapptax.com/"
                disabled={true}
                tooltip={tr({
                  en: "Comin soon",
                  fr: "Bientôt",
                })}

                label={tr({
                  en: "Sign in",
                  fr: "Se connecter",
                })}
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
